import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ParticipantBox, REMOTE_INTERACTION } from '@ysura/common';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useInteraction } from '@/hooks';

import { NoHardwareAccess } from './NoHardwareAccess';

export const PreviewVideo = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [previousVideoRef, setPreviousVideoRef] =
    useState<HTMLVideoElement | null>(null);
  const {
    isCameraActive,
    selectedVideoInput,
    cameraPermissionState,
    microphonePermissionState,
  } = useInteraction();

  const shouldRenderVideo =
    isCameraActive && cameraPermissionState === 'granted';

  const shouldRenderErrorMessage =
    cameraPermissionState === 'denied' &&
    microphonePermissionState === 'denied';

  const stopStream = (stream: MediaStream) => {
    const tracks = stream?.getTracks?.() ?? [];
    tracks.forEach((track) => {
      track?.stop();
    });
  };

  useEffect(() => {
    if (shouldRenderVideo && videoRef.current) {
      setPreviousVideoRef(videoRef.current);
    }
  }, [shouldRenderVideo]);

  // Get the currently selected camera & start the stream
  useEffect(() => {
    if (
      cameraPermissionState === 'granted' &&
      isCameraActive &&
      selectedVideoInput
    ) {
      /**
       * If a stream exists, and the selectedVideoInput has been changed, stop the stream coming
       * from the previously selectedVideoInput. This will ensure that the light turns
       * off for any camera that is not currently being used.
       */
      if (videoRef?.current?.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        stopStream(stream);
      }
      // get the latest selected video device
      navigator.mediaDevices
        .getUserMedia({
          video: {
            deviceId: {
              exact: selectedVideoInput,
            },
          },
        })
        .then((stream) => {
          // pass the stream to the video element & play
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            const playPromise = videoRef.current.play();

            if (playPromise !== undefined) {
              /**
               * Catching the error for cypress test only. I have not noticed
               * that we have any issues when using the app ourselves.
               * https://developer.chrome.com/blog/play-request-was-interrupted/
               */
              playPromise.catch((error) => console.log(error));
            }
          }
        })
        .catch(() => {
          /**
           * on some Android phones the newly selected video doesn't play,
           * and we end up in this catch block
           */
          // TODO: this issue will be addressed in the future
          videoRef.current?.pause();
        });
    }
  }, [selectedVideoInput, cameraPermissionState, isCameraActive]);

  // Stop the stream if camera is inactive
  useEffect(() => {
    if (!isCameraActive && previousVideoRef) {
      const stream = previousVideoRef.srcObject as MediaStream;
      stopStream(stream);
    }
  }, [isCameraActive, previousVideoRef]);

  /**
   * Cleanup with layoutEffect. layoutEffect is needed here as we need
   * to read the videoRef value before it's destroyed
   */
  useLayoutEffect(() => {
    /**
     * Copying the ref to a local variable and use it inside the cleanup function.
     * This is done as videoRef will likely have changed by the time this effect cleanup function runs.
     */
    const cleanupVideoRef = videoRef;

    return () => {
      if (cleanupVideoRef.current?.srcObject) {
        const stream = cleanupVideoRef.current.srcObject as MediaStream;
        stopStream(stream);
      }
    };
  }, []);

  if (shouldRenderErrorMessage) {
    return <NoHardwareAccess />;
  }

  return (
    <Container>
      {/* playsInline is necessary for iOS devices, so the video doesn't open native video player on click */}
      {shouldRenderVideo && (
        <OwnVideo ref={videoRef} playsInline data-testid="own-video">
          <track kind="captions" />
        </OwnVideo>
      )}
      {!shouldRenderVideo && (
        <VideoOffBox>
          <ParticipantBox size={'large'} />
        </VideoOffBox>
      )}
    </Container>
  );
};

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  height: '100%',
  width: '100%',
  borderRadius: Number(theme.shape.borderRadius) * 2,
  overflow: 'hidden',
  background: theme.palette.grey[700],
}));

const OwnVideo = styled('video')(({ theme }) => ({
  height: '100%',
  width: '100%',
  objectFit: 'cover',
  borderRadius: Number(theme.shape.borderRadius) * 2,
  transform: 'scaleX(-1)',
}));

const VideoOffBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: Number(theme.shape.borderRadius) * 2,
  aspectRatio: '1/1',
  height: REMOTE_INTERACTION.COMMON.VIDEO_OFF_PARTICIPANT_INITIALS_BOX_SIZE,
}));
